import {
  Card,
  CardContent,
  CardActionArea,
  Stack,
  Typography,
  Box,
  Avatar,
} from "@mui/material";
import { Medication, Assignment } from "@mui/icons-material";

/**
 * Tipo de conteúdo que pode ser selecionado
 */
export type ContentType = "medications" | "exams" | null;

interface TypeSelectorProps {
  onSelectType: (type: ContentType) => void;
}

/**
 * Componente para seleção inicial do tipo de conteúdo (Medicamentos ou Exames)
 * Apresenta cards visuais grandes e intuitivos para melhorar a experiência do usuário
 */
export function TypeSelector({ onSelectType }: TypeSelectorProps) {
  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      {/* Título e descrição */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          O que você deseja acompanhar?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Selecione o tipo de conteúdo para visualizar e gerenciar
        </Typography>
      </Box>

      {/* Cards de seleção */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={3}
        sx={{ justifyContent: "center", alignItems: "stretch" }}
      >
        {/* Card de Medicamentos */}
        <Card
          sx={{
            flex: 1,
            maxWidth: { xs: "100%", sm: "400px" },
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.15)",
            },
          }}
        >
          <CardActionArea
            onClick={() => onSelectType("medications")}
            sx={{
              p: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "200px",
            }}
          >
            <CardContent sx={{ textAlign: "center", width: "100%" }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mx: "auto",
                  mb: 2,
                  bgcolor: "primary.main",
                  color: "white",
                }}
              >
                <Medication sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Medicamentos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Visualize e gerencie prescrições de medicamentos, acompanhe
                tratamentos e envie mensagens de acompanhamento aos pacientes
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>

        {/* Card de Exames */}
        <Card
          sx={{
            flex: 1,
            maxWidth: { xs: "100%", sm: "400px" },
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.15)",
            },
          }}
        >
          <CardActionArea
            onClick={() => onSelectType("exams")}
            sx={{
              p: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "200px",
            }}
          >
            <CardContent sx={{ textAlign: "center", width: "100%" }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mx: "auto",
                  mb: 2,
                  bgcolor: "secondary.main",
                  color: "white",
                }}
              >
                <Assignment sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Exames
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Visualize solicitações de exames, acompanhe resultados e envie
                mensagens de acompanhamento aos pacientes
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      </Stack>
    </Stack>
  );
}
